import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  StatusView,
  hasAuthCallbackParams,
  isSignInError,
  traceButtonClass,
  type SignInAppState,
  type SignInErrorCode,
} from '@traceio-org/auth-ui';
import type { ProductKey } from '@traceio-org/platform-contracts';
import { useBootstrap, useHub } from '../app/context';
import { loginRoute, useDecideAndFollow, usePageHeading } from '../app/hooks';
import { loginPath } from '../navigation/decision';
import { HubFrame, PageHeading } from '../app/ui';
import { SigningInPage, signingInProduct } from '../app/signing-in';
import { forcedProductOf } from '../security/host';
import { safeHubPath } from '../security/hub-path';
import { clearSilentRestore, restoringSettings } from '../app/session-restore';
import { SettingsPlaceholder } from '../app/settings-placeholder';

type CallbackState =
  | { kind: 'working' }
  | { kind: 'not-configured' }
  | { kind: 'nothing-to-complete' }
  | { kind: 'failed'; code: SignInErrorCode; productKey: ProductKey | null }
  | { kind: 'decided'; appState: SignInAppState };

const FAILURE_COPY: Record<SignInErrorCode, { title: string; body: string }> = {
  access_denied: {
    title: 'Sign-in did not finish',
    body: 'The sign-in was cancelled, or your account was not given access. You can try again or use a different account.',
  },
  login_required: {
    title: 'Sign in again',
    body: 'Your sign-in session ended before it finished.',
  },
  invalid_state: {
    title: 'This sign-in link has expired',
    body: 'A sign-in link works once, in the browser that started it. Start again from the sign-in page.',
  },
  network: {
    title: 'We could not reach the sign-in service',
    body: 'Check your connection, then try again from the sign-in page.',
  },
  organization_mismatch: {
    title: 'Sign-in did not finish',
    body: 'You signed in to a different organization than the one this page asked for. Start again from the sign-in page.',
  },
  unknown: {
    title: 'Sign-in did not finish',
    body: 'Something went wrong while completing sign-in. Start again from the sign-in page.',
  },
};

function FailureView({ code, productKey }: { code: SignInErrorCode; productKey: ProductKey | null }) {
  const copy = FAILURE_COPY[code];
  const headingRef = usePageHeading(copy.title);
  return (
    <StatusView
      tone={code === 'network' ? 'warning' : 'error'}
      title={copy.title}
      headingRef={headingRef}
      primaryAction={
        <Link className={traceButtonClass('primary', { block: true })} to={loginRoute(productKey)}>
          Back to sign-in
        </Link>
      }
    >
      <p>{copy.body}</p>
    </StatusView>
  );
}

/**
 * /auth/callback: finishes the Auth0 redirect, then asks platform-api where to go. It never retries on
 * its own: every failure ends in a state with a button back to the right sign-in page.
 */
export function CallbackPage() {
  const deps = useHub();
  const { host } = useBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const { run: runDecide, failed: decideFailed } = useDecideAndFollow();
  const [state, setState] = useState<CallbackState>({ kind: 'working' });
  // Read once, before the callback clears the record: the callback of a settings page's silent restore keeps the
  // settings placeholder on screen (the same markup as the page's first paint) instead of the signing-in screen.
  const [settingsRestore] = useState(() => restoringSettings());
  const started = useRef(false);
  const fallbackProduct = forcedProductOf(host);

  const decideFor = useCallback(
    async (appState: SignInAppState) => {
      setState({ kind: 'decided', appState });
      await runDecide(
        {
          ...(appState.productKey ? { productKey: appState.productKey } : {}),
          ...(appState.returnPath ? { returnPath: appState.returnPath } : {}),
          ...(appState.contextId ? { contextId: appState.contextId } : {}),
        },
        { reauthenticated: appState.reauthenticate, replace: true },
      );
    },
    [runDecide],
  );

  useEffect(() => {
    // Runs once per page load: StrictMode's second effect run must not complete the sign-in twice.
    if (started.current) return;
    started.current = true;
    const auth0 = deps.sessions.auth0;
    if (!auth0) {
      setState({ kind: 'not-configured' });
      return;
    }
    if (!hasAuthCallbackParams(location.search)) {
      setState({ kind: 'nothing-to-complete' });
      return;
    }
    void (async () => {
      let appState: SignInAppState;
      try {
        appState = await auth0.completeSignIn();
      } catch (error) {
        const code = isSignInError(error) ? error.code : 'unknown';
        const productKey = (isSignInError(error) ? error.appState.productKey : undefined) ?? fallbackProduct;
        const silentHubPath =
          isSignInError(error) && error.appState.silent ? safeHubPath(error.appState.hubPath) : undefined;
        if (silentHubPath) {
          // A reload's silent restore (app/session-restore.tsx) that found no usable session: back to the very page,
          // which then shows its own sign-in. silentTried keeps it from asking silently again (no loop).
          navigate(silentHubPath, { replace: true, state: { silentTried: true, silentFailed: true } });
          return;
        }
        if (isSignInError(error) && error.appState.silent) {
          // A silent attempt (prompt none) that found no usable session, or needs the person (MFA, consent):
          // not a failure, the sign-in page with its methods is the answer. silentTried keeps that page from
          // asking silently again, so this can never loop.
          navigate(loginPath(productKey, error.appState.returnPath), {
            replace: true,
            state: { silentTried: true, silentFailed: true },
          });
          return;
        }
        setState({ kind: 'failed', code, productKey: productKey ?? null });
        return;
      }
      deps.sessions.activate(auth0);
      // Signed in: a later reload may restore silently again.
      clearSilentRestore();
      const hubPath = safeHubPath(appState.hubPath);
      if (hubPath) {
        // The page learns that this sign-in answered a reauthenticate requirement, so it can refuse to
        // restart sign-in for the same reason again (no loop).
        navigate(hubPath, {
          replace: true,
          ...(appState.reauthenticate ? { state: { reauthenticated: appState.reauthenticate } } : {}),
        });
        return;
      }
      await decideFor(appState);
    })();
  }, [deps, location.search, navigate, decideFor, fallbackProduct]);

  if (state.kind === 'failed') {
    return (
      <HubFrame productKey={state.productKey}>
        <FailureView code={state.code} productKey={state.productKey} />
      </HubFrame>
    );
  }

  if (state.kind === 'not-configured' || state.kind === 'nothing-to-complete') {
    const title =
      state.kind === 'not-configured'
        ? 'Sign-in is not configured in this environment'
        : 'There is no sign-in to complete';
    return (
      <HubFrame productKey={fallbackProduct}>
        <PageHeading title={title} lead="Start from the sign-in page." />
        <Link className={traceButtonClass('primary', { block: true })} to={loginRoute(fallbackProduct)}>
          Go to sign-in
        </Link>
      </HubFrame>
    );
  }

  if (state.kind === 'decided' && decideFailed) {
    const appState = state.appState;
    const productKey = appState.productKey ?? fallbackProduct;
    return (
      <HubFrame productKey={productKey}>
        <DecideFailedView productKey={productKey} onRetry={() => void decideFor(appState)} />
      </HubFrame>
    );
  }

  if (settingsRestore) return <SettingsPlaceholder brand={host.kind === 'trace'} />;

  // The product this sign-in is for is known before Auth0 answers (remembered when it started), so the heading is
  // right from the first frame and stays put until the next page.
  const productKey =
    (state.kind === 'decided' ? state.appState.productKey : undefined) ??
    signingInProduct() ??
    fallbackProduct;
  return <SigningInPage productKey={productKey} />;
}

function DecideFailedView({ productKey, onRetry }: { productKey: ProductKey | null; onRetry: () => void }) {
  const title = 'We could not finish signing you in';
  const headingRef = usePageHeading(title);
  return (
    <StatusView
      tone="error"
      title={title}
      headingRef={headingRef}
      primaryAction={{ label: 'Try again', onClick: onRetry }}
      secondaryAction={
        <Link className={traceButtonClass('quiet', { block: true })} to={loginRoute(productKey)}>
          Back to sign-in
        </Link>
      }
    >
      <p>Your sign-in worked, but Trace could not work out where to send you next.</p>
    </StatusView>
  );
}
